import createClient from "openapi-fetch";
import type {
  ApiError,
  Attachment,
  AuditEvent,
  DocumentRequest,
  DocumentRequestDetail,
  DocumentTemplate,
  DocumentTemplateField,
  DocumentTemplateFieldWrite,
  TemplateBlank,
  DocumentType,
  DocumentTypeWrite,
  FeeBucket,
  FeeProposal,
  HealthComponent,
  HealthReport,
  Integration,
  LoginAttempt,
  FeeProposalStatus,
  PaymentRow,
  PlaceTotal,
  PolicyDocument,
  RefItem,
  RefList,
  RequestStatus,
  StaffMember,
  StatsSummary,
  StatsBreakdown,
  StatsBreakdownRow,
  AdminRequestRow,
  StatusCount,
  StatusHistoryEntry,
  TemplateStandardField,
  User,
  UserPage,
  DocumentLayout,
  LayoutBlock,
  LayoutField,
  LayoutSignatureColumn,
  Incident,
  IncidentStatus,
  IncidentCategory,
  Consultation,
  ConsultationStatus,
  ConsultationTopic,
  ConsultationContactMode,
  ConsultationStats,
  PositionCode,
  PermissionMatrix,
  PermissionDef,
  PositionPermission,
  PermissionOverride,
  PIIAccessStatus,
  PIIAccessGrant,
  PIIAccessGrantList,
  RequestPIIAccess,
  components,
  paths,
} from "@sdds/api-types";
import { getToken } from "./auth";
import { sessionMiddleware } from "./session";

export type {
  ApiError,
  Attachment,
  AuditEvent,
  DocumentRequest,
  DocumentRequestDetail,
  DocumentTemplate,
  DocumentTemplateField,
  DocumentTemplateFieldWrite,
  TemplateBlank,
  DocumentType,
  DocumentTypeWrite,
  FeeBucket,
  FeeProposal,
  HealthComponent,
  HealthReport,
  Integration,
  LoginAttempt,
  FeeProposalStatus,
  PaymentRow,
  PlaceTotal,
  PolicyDocument,
  RefItem,
  RefList,
  RequestStatus,
  StaffMember,
  StatsSummary,
  StatsBreakdown,
  StatsBreakdownRow,
  AdminRequestRow,
  StatusCount,
  StatusHistoryEntry,
  TemplateStandardField,
  User,
  UserPage,
  DocumentLayout,
  LayoutBlock,
  LayoutField,
  LayoutSignatureColumn,
  Incident,
  IncidentStatus,
  IncidentCategory,
  Consultation,
  ConsultationStatus,
  ConsultationTopic,
  ConsultationContactMode,
  ConsultationStats,
  PositionCode,
  PermissionMatrix,
  PermissionDef,
  PositionPermission,
  PermissionOverride,
  PIIAccessStatus,
  PIIAccessGrant,
  PIIAccessGrantList,
  RequestPIIAccess,
};

// ขั้นรับรองของนายบ้าน (ADR-0031) — @sdds/api-types ยังไม่ได้ re-export ชื่อพวกนี้ จึงดึงจาก schema ตรง ๆ
export type VillageCertification = components["schemas"]["VillageCertification"];
export type VillageCertStatus = components["schemas"]["VillageCertStatus"];
export type HistoryEvent = components["schemas"]["HistoryEvent"];

/**
 * HTTP client ที่ type จาก apps/api/api/openapi.yaml (ผ่าน @sdds/api-types)
 *
 * baseUrl ว่าง = same-origin: ตอน dev vite proxy /v1 ไปที่ API, ตอน prod ให้ reverse proxy ทำแบบเดียวกัน
 * ตั้ง VITE_API_BASE_URL ตอน build เฉพาะเมื่อ client ต้องเรียก API คนละ origin (ต้องเปิด CORS ฝั่ง API)
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export const api = createClient<paths>({ baseUrl: API_BASE_URL });
// ต่ออายุ access token ให้เองทุก request ที่ใช้สิทธิ์ (ดู lib/session.ts)
api.use(sessionMiddleware);

/** header สำหรับ endpoint ที่ต้องสิทธิ์ (admin/officer) — ใช้ token จาก localStorage */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** แปลง error body ของ API (หรือกรณีไม่มี body) เป็นข้อความสำหรับแสดงผล */
export function describeError(error: ApiError | undefined, status: number): string {
  if (error?.error) {
    const { code, message } = error.error;
    return `${code} — ${message}`;
  }
  switch (status) {
    case 401:
      return "UNAUTHORIZED — token ບໍ່ຖືກຕ້ອງ ຫຼື ໝົດອາຍຸ";
    case 403:
      return "FORBIDDEN — ສິດບໍ່ພຽງພໍ";
    case 409:
      return "CONFLICT — ຂໍ້ມູນຊ້ຳ ຫຼື version ບໍ່ກົງ";
    case 501:
      return "NOT_IMPLEMENTED — endpoint ນີ້ຍັງບໍ່ພ້ອມ";
    default:
      return status === 0 ? "ຕິດຕໍ່ API ບໍ່ໄດ້" : `HTTP ${status}`;
  }
}
